import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { usePersonalization } from '@/hooks/usePersonalization';
import { Brain, TrendingUp, Target, Zap, BarChart3, Eye, ShoppingBag } from 'lucide-react';
import { motion } from 'framer-motion';

const AIPersonalizationSettings = () => {
  const { browsingHistory, personalizationScore } = usePersonalization();
  const [aiPreferences, setAiPreferences] = useState({
    enable_recommendations: true,
    smart_search: true,
    price_suggestions: true,
    trend_alerts: true,
    seasonal_suggestions: true,
    brand_recommendations: true,
    category_learning: true,
    behavior_analysis: true
  });

  const categoryAffinities = personalizationScore?.category_affinity || {};
  const engagementScore = personalizationScore?.engagement_score || 0;

  return (
    <div className="space-y-6">
      {/* AI Status Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="bg-gradient-to-br from-cyan-500/5 to-blue-500/5 border-cyan-500/20">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <Brain className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  AI Personalization Hub
                  <Badge className="bg-cyan-100 text-cyan-800 text-xs">
                    <Zap className="h-3 w-3 mr-1" />
                    Active
                  </Badge>
                </CardTitle>
                <CardDescription>Smart recommendations powered by your behavior</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-white/50 border border-cyan-200">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="h-4 w-4 text-cyan-600" />
                  <span className="text-sm font-medium">Data Points</span>
                </div>
                <p className="text-2xl font-bold text-cyan-700">{browsingHistory.length}</p>
                <p className="text-xs text-muted-foreground">Analyzed sessions</p>
              </div>
              
              <div className="p-4 rounded-lg bg-white/50 border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium">Accuracy</span>
                </div>
                <p className="text-2xl font-bold text-blue-700">{Math.round(engagementScore * 100)}%</p>
                <p className="text-xs text-muted-foreground">Recommendation match</p>
              </div>
              
              <div className="p-4 rounded-lg bg-white/50 border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-4 w-4 text-purple-600" />
                  <span className="text-sm font-medium">Categories</span>
                </div>
                <p className="text-2xl font-bold text-purple-700">{Object.keys(categoryAffinities).length}</p>
                <p className="text-xs text-muted-foreground">Learned preferences</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Category Insights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <Card className="bg-gradient-to-br from-background to-muted/30 border-border/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Eye className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <CardTitle>Interest Analysis</CardTitle>
                <CardDescription>Your personalized category preferences</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(categoryAffinities).map(([category, score]) => (
              <div key={category} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium capitalize">{category}</span>
                  <Badge variant="outline" className="text-xs">
                    {Math.round((score as number) * 100)}% match
                  </Badge>
                </div>
                <Progress value={(score as number) * 100} className="h-2" />
              </div>
            ))}
          </CardContent>
        </Card>
      </motion.div>

      {/* AI Features Control */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <Card className="bg-gradient-to-br from-background to-muted/30 border-border/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                <ShoppingBag className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <CardTitle>AI Features</CardTitle>
                <CardDescription>Control what AI learns and suggests</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                  Recommendations
                </h4>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Smart Recommendations</Label>
                    <p className="text-sm text-muted-foreground">AI-powered product suggestions</p>
                  </div>
                  <Switch
                    checked={aiPreferences.enable_recommendations}
                    onCheckedChange={(checked) => setAiPreferences(prev => ({ ...prev, enable_recommendations: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Smart Search</Label>
                    <p className="text-sm text-muted-foreground">Enhanced search with AI insights</p>
                  </div>
                  <Switch
                    checked={aiPreferences.smart_search}
                    onCheckedChange={(checked) => setAiPreferences(prev => ({ ...prev, smart_search: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Price Suggestions</Label>
                    <p className="text-sm text-muted-foreground">AI-powered price alerts</p>
                  </div>
                  <Switch
                    checked={aiPreferences.price_suggestions}
                    onCheckedChange={(checked) => setAiPreferences(prev => ({ ...prev, price_suggestions: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Trend Alerts</Label>
                    <p className="text-sm text-muted-foreground">Notifications about trending items</p>
                  </div>
                  <Switch
                    checked={aiPreferences.trend_alerts}
                    onCheckedChange={(checked) => setAiPreferences(prev => ({ ...prev, trend_alerts: checked }))}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                  Learning & Analysis
                </h4>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Seasonal Suggestions</Label>
                    <p className="text-sm text-muted-foreground">Season-based recommendations</p>
                  </div>
                  <Switch
                    checked={aiPreferences.seasonal_suggestions}
                    onCheckedChange={(checked) => setAiPreferences(prev => ({ ...prev, seasonal_suggestions: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Brand Learning</Label>
                    <p className="text-sm text-muted-foreground">Learn your brand preferences</p>
                  </div>
                  <Switch
                    checked={aiPreferences.brand_recommendations}
                    onCheckedChange={(checked) => setAiPreferences(prev => ({ ...prev, brand_recommendations: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Category Learning</Label>
                    <p className="text-sm text-muted-foreground">Analyze browsing patterns</p>
                  </div>
                  <Switch
                    checked={aiPreferences.category_learning}
                    onCheckedChange={(checked) => setAiPreferences(prev => ({ ...prev, category_learning: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Behavior Analysis</Label>
                    <p className="text-sm text-muted-foreground">Deep behavior insights</p>
                  </div>
                  <Switch
                    checked={aiPreferences.behavior_analysis}
                    onCheckedChange={(checked) => setAiPreferences(prev => ({ ...prev, behavior_analysis: checked }))}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Save Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Zap className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">AI preferences updated</p>
                  <p className="text-sm text-muted-foreground">Changes take effect immediately</p>
                </div>
              </div>
              <Button className="bg-gradient-to-r from-primary to-secondary hover:shadow-lg transition-all duration-300">
                Save AI Settings
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default AIPersonalizationSettings;